import React, { useRef } from "react";
// Using simple HTML icons instead of react-icons for TypeScript compatibility
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import StaticBarChart from "./StaticBarChart";

interface ReportModalProps {
  isOpen: boolean;
  onClose: () => void;
  results: any;
  formData?: any;
  detailedFormData?: any;
  isDetailed?: boolean; // Add flag to distinguish between quick and detailed
}

const ReportModal = ({ isOpen, onClose, results, formData, detailedFormData, isDetailed = false }: ReportModalProps) => {
  const reportRef = useRef<HTMLDivElement>(null);

  const generatePDF = async () => {
    if (!reportRef.current) return;

    try {
      // Add a small delay to ensure all styles are loaded
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // Force a reflow to ensure consistent layout
      reportRef.current.style.display = 'none';
      void reportRef.current.offsetHeight; // Trigger reflow
      reportRef.current.style.display = 'block';
      
      // Wait a bit more for layout stabilization
      await new Promise(resolve => setTimeout(resolve, 300));

      const canvas = await html2canvas(reportRef.current, {
        scale: 1.5, // Increased scale for better quality
        useCORS: true,
        allowTaint: true,
        backgroundColor: "#ffffff",
        logging: false,
        height: reportRef.current.scrollHeight,
        width: reportRef.current.scrollWidth,
        foreignObjectRendering: false, // More consistent rendering
        removeContainer: true,
        onclone: (clonedDoc) => {
          // Add page break avoidance styles to cloned document
          const style = clonedDoc.createElement('style');
          style.textContent = `
            * { box-sizing: border-box; }
            .avoid-break { page-break-inside: avoid !important; }
            .chart-section { page-break-inside: avoid !important; }
            .metrics-section { page-break-inside: avoid !important; }
            body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; }
          `;
          clonedDoc.head.appendChild(style);
          
          // Ensure all elements are properly styled
          const elements = clonedDoc.querySelectorAll('*');
          elements.forEach(el => {
            if (el instanceof HTMLElement) {
              (el.style as any).webkitFontSmoothing = 'antialiased';
              (el.style as any).mozOsxFontSmoothing = 'grayscale';
            }
          });
        }
      });

      const imgData = canvas.toDataURL("image/png", 0.95);
      const pdf = new jsPDF("p", "mm", "a4");
      
      const imgWidth = 210; // A4 width in mm
      const pageHeight = 297; // A4 height in mm
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      let heightLeft = imgHeight;
      let position = 0;

      // Add first page
      pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;

      // Add additional pages if needed
      while (heightLeft >= 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }

      // Different file names for quick vs detailed calculator
      const fileName = isDetailed ? "fownd-comprehensive-roi-analysis.pdf" : "fownd-quick-roi-analysis.pdf";
      pdf.save(fileName);
    } catch (error) {
      console.error("Error generating PDF:", error);
    }
  };

  if (!isOpen) return null;

  const currentDate = new Date().toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-auto">
        {/* Modal Header */}
        <div className="flex justify-between items-center p-6 border-b">
          <h2 className="text-2xl font-bold text-slate-800">
            {isDetailed ? "Comprehensive ROI Analysis Report" : "ROI Analysis Report"}
          </h2>
          <div className="flex gap-3">
            <button
              onClick={generatePDF}
              className="bg-[#1e3d3b] text-white px-4 py-2 rounded-lg hover:bg-[#2a5a56] transition-colors flex items-center gap-2"
            >
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clipRule="evenodd" />
              </svg>
              Download PDF
            </button>
            <button
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700 transition-colors"
            >
              <span className="text-2xl">×</span>
            </button>
          </div>
        </div>

        {/* PDF Content */}
        <div className="flex justify-center py-4">
          <div 
            ref={reportRef} 
            className="bg-white" 
            style={{ 
              width: "210mm", 
              minHeight: "auto", 
              padding: "15mm",
              fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
              lineHeight: "1.5",
              fontSize: "14px"
            }}
          >
            {/* Header with Logo */}
            <div className="flex items-center justify-between mb-8 pb-4 border-b-2 border-[#1e3d3b]">
              <div className="flex items-center gap-4">
                <img src="/fownd.png" alt="Fownd Logo" className="h-12" />
                <div>
                  <h1 className="text-2xl font-bold text-[#1e3d3b]">
                    {isDetailed ? "Comprehensive Return on Investment" : "Return on Investment"}
                  </h1>
                  <p className="text-base text-slate-600">
                    with <span className="font-bold">Notation</span> by Fownd
                  </p>
                  <p className="text-sm text-slate-500">
                    Generated on: {currentDate}
                  </p>
                </div>
              </div>
              <div className="text-right">
                <a 
                  href="https://notation.fownd.care/" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="bg-[#619ed3] hover:bg-[#5a8bc2] text-white px-6 py-3 rounded-lg font-semibold text-sm transition-colors inline-block"
                  style={{ textDecoration: 'none', color: 'white' }}
                >
                  Try Notation Now!
                </a>
                <p className="text-xs text-slate-600 mt-2">
                  Visit: https://notation.fownd.care/
                </p>
              </div>
            </div>

            {/* Executive Summary */}
            <div className="mb-8">
              <h2 className="text-xl font-bold text-slate-800 mb-4">Executive Summary</h2>
              <p className="text-slate-700 leading-relaxed">
                This report presents a comprehensive analysis of the potential return on investment (ROI) from implementing Fownd's AI-powered clinical documentation solution within your physical therapy practice. The projections are based on a combination of industry benchmarks and the specific operational parameters of your clinic.
              </p>
            </div>

            {/* Quick Calculator Results */}
            {formData && (
              <div className="mb-8">
                <h2 className="text-xl font-bold text-slate-800 mb-6">Quick ROI Estimation</h2>
                
                {/* Form Inputs */}
                <div className="mb-6">
                  <h3 className="text-lg font-semibold text-slate-700 mb-3">Practice Parameters</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-slate-50 p-4 rounded-lg">
                      <p className="text-sm text-slate-500 mb-1">Physical Therapists</p>
                      <p className="text-xl font-bold text-slate-800">{formData.physicalTherapists || "N/A"}</p>
                    </div>
                    <div className="bg-slate-50 p-4 rounded-lg">
                      <p className="text-sm text-slate-500 mb-1">Daily Sessions</p>
                      <p className="text-xl font-bold text-slate-800">{formData.sessions || "N/A"}</p>
                    </div>
                    <div className="bg-slate-50 p-4 rounded-lg">
                      <p className="text-sm text-slate-500 mb-1">Documentation Time</p>
                      <p className="text-xl font-bold text-slate-800">
                        {formData.documentationTime || "N/A"}
                      </p>
                    </div>
                    <div className="bg-slate-50 p-4 rounded-lg">
                      <p className="text-sm text-slate-500 mb-1">Average Salary</p>
                      <p className="text-xl font-bold text-slate-800">${(formData.salary || "N/A").toString().replace(/^\$+/, '')}</p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* ROI Analysis Results Section */}
            <div className="mb-8">
              <h2 className="text-xl font-bold text-slate-800 mb-6">ROI Analysis Results</h2>
              
              {/* Chart and Key Metrics Layout */}
              <div className="flex gap-8 mb-8 chart-section avoid-break">
                {/* Cost Comparison Chart - Left Side */}
                <div className="w-1/2">
                  <StaticBarChart 
                    withFownd={results.metrics.revenueGain * 0.6} // Estimated cost with Fownd
                    withoutFownd={results.metrics.revenueGain} // Estimated cost without Fownd
                  />
                </div>
                
                {/* Key Metrics - Right Side */}
                <div className="w-1/2 space-y-4">
                  <h3 className="text-lg font-semibold text-slate-700 mb-4">Key Metrics</h3>
                  <div className="space-y-3">
                    <div className="bg-slate-50 p-4 rounded-lg">
                      <div className="text-sm text-slate-500">Cost Savings</div>
                      <div className="text-2xl font-bold text-[#619ed3]">
                        ${(results.metrics.costSavings || (results.metrics.revenueGain * 0.4)).toLocaleString()}
                      </div>
                    </div>
                    <div className="bg-slate-50 p-4 rounded-lg">
                      <div className="text-sm text-slate-500">Time Efficiency</div>
                      <div className="text-2xl font-bold text-[#619ed3]">
                        {results.metrics.hoursSaved.toFixed(0)} hours saved
                      </div>
                    </div>
                  </div>
                  
                  <h3 className="text-lg font-semibold text-slate-700 mb-4 pt-4">ROI Analysis</h3>
                  <div className="space-y-3">
                    <div className="bg-slate-50 p-4 rounded-lg">
                      <div className="text-sm text-slate-500">ROI Percentage</div>
                      <div className="text-2xl font-bold text-[#619ed3]">84.1%</div>
                    </div>
                    <div className="bg-slate-50 p-4 rounded-lg">
                      <div className="text-sm text-slate-500">Payback Period</div>
                      <div className="text-2xl font-bold text-[#619ed3]">~6 months</div>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Overall Clinic Estimates */}
              <div className="mb-6 metrics-section avoid-break">
                <h3 className="text-lg font-bold text-slate-800 mb-4 flex items-center">
                  <svg className="mr-2 w-5 h-5 text-[#619ed3]" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M4 4a2 2 0 00-2 2v8a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2H4zm2 2h8v8H6V6z" clipRule="evenodd" />
                  </svg>
                  Overall Clinic Estimates
                </h3>
                <div className="grid grid-cols-4 gap-4">
                  <div className="bg-slate-50 p-4 rounded-lg text-center">
                    <div className="text-xl font-bold text-[#619ed3] mb-1">
                      ${results.metrics.revenueGain.toLocaleString()}
                    </div>
                    <div className="text-xs text-slate-500">Revenue Gain</div>
                  </div>
                  <div className="bg-slate-50 p-4 rounded-lg text-center">
                    <div className="text-xl font-bold text-[#619ed3] mb-1">
                      {results.metrics.hoursSaved.toFixed(0)} hrs
                    </div>
                    <div className="text-xs text-slate-500">Hours Saved Annually</div>
                  </div>
                  <div className="bg-slate-50 p-4 rounded-lg text-center">
                    <div className="text-xl font-bold text-[#619ed3] mb-1">
                      {results.metrics.patientRetention}%
                    </div>
                    <div className="text-xs text-slate-500">Patient Retention</div>
                  </div>
                  <div className="bg-slate-50 p-4 rounded-lg text-center">
                    <div className="text-xl font-bold text-[#619ed3] mb-1">
                      {results.metrics.documentationAccuracy}%
                    </div>
                    <div className="text-xs text-slate-500">Documentation Accuracy</div>
                  </div>
                </div>
              </div>

              {/* Per Clinician Estimates */}
              <div className="mb-6 metrics-section avoid-break">
                <h3 className="text-lg font-bold text-slate-800 mb-4 flex items-center">
                  <svg className="mr-2 w-5 h-5 text-[#619ed3]" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" />
                  </svg>
                  Per Clinician Estimates
                </h3>
                <div className="grid grid-cols-3 gap-4">
                  <div className="bg-slate-50 p-4 rounded-lg text-center">
                    <div className="text-xl font-bold text-[#619ed3] mb-1">
                      ${results.perTherapist.revenueGain.toLocaleString(undefined, {
                        maximumFractionDigits: 0,
                      })}
                    </div>
                    <div className="text-xs text-slate-500">Revenue Gain</div>
                  </div>
                  <div className="bg-slate-50 p-4 rounded-lg text-center">
                    <div className="text-xl font-bold text-[#619ed3] mb-1">
                      {results.perTherapist.hoursSaved.toFixed(0)} hrs
                    </div>
                    <div className="text-xs text-slate-500">Time Saved</div>
                  </div>
                  <div className="bg-slate-50 p-4 rounded-lg text-center">
                    <div className="text-xl font-bold text-[#619ed3] mb-1">
                      ${results.perTherapist.costSavings.toLocaleString(undefined, {
                        maximumFractionDigits: 0,
                      })}
                    </div>
                    <div className="text-xs text-slate-500">Cost Savings</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Detailed Calculator Results */}
            {detailedFormData && (
              <>
                <div className="mb-8">
                  <h2 className="text-2xl font-bold text-slate-800 mb-4">Detailed Analysis Parameters</h2>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-slate-50 p-4 rounded-lg">
                      <p className="text-sm text-slate-500">Annual Audits</p>
                      <p className="text-lg font-semibold">{detailedFormData.annualAudits || "N/A"}</p>
                    </div>
                    <div className="bg-slate-50 p-4 rounded-lg">
                      <p className="text-sm text-slate-500">Monthly Claims</p>
                      <p className="text-lg font-semibold">{detailedFormData.monthlyClaims || "N/A"}</p>
                    </div>
                    <div className="bg-slate-50 p-4 rounded-lg">
                      <p className="text-sm text-slate-500">Denial Rate</p>
                      <p className="text-lg font-semibold">{detailedFormData.denialRate || "N/A"}%</p>
                    </div>
                    <div className="bg-slate-50 p-4 rounded-lg">
                      <p className="text-sm text-slate-500">Appeal Success Rate</p>
                      <p className="text-lg font-semibold">{detailedFormData.appealSuccessRate || "N/A"}%</p>
                    </div>
                  </div>
                </div>

                {/* Clinic Financial Impact Results */}
                <div className="mb-8">
                  <h2 className="text-2xl font-bold text-slate-800 mb-6">Clinic Financial Impact Results</h2>
                  
                  {/* Selected Inputs Summary */}
                  <div className="mb-6">
                    <h3 className="text-lg font-semibold text-slate-700 mb-3">Selected Financial Parameters</h3>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div className="bg-blue-50 p-3 rounded">
                        <span className="text-slate-600">Annual Audits:</span> <span className="font-semibold">{detailedFormData.annualAudits}</span>
                      </div>
                      <div className="bg-blue-50 p-3 rounded">
                        <span className="text-slate-600">Audit Prep Hours:</span> <span className="font-semibold">{detailedFormData.auditPrepHours} hours</span>
                      </div>
                      <div className="bg-blue-50 p-3 rounded">
                        <span className="text-slate-600">Staff Hourly Rate:</span> <span className="font-semibold">${detailedFormData.auditStaffSalary}/hour</span>
                      </div>
                      <div className="bg-blue-50 p-3 rounded">
                        <span className="text-slate-600">Monthly Claims:</span> <span className="font-semibold">{parseInt(detailedFormData.monthlyClaims || "0").toLocaleString()}</span>
                      </div>
                      <div className="bg-blue-50 p-3 rounded">
                        <span className="text-slate-600">Denial Rate:</span> <span className="font-semibold">{detailedFormData.denialRate}%</span>
                      </div>
                      <div className="bg-blue-50 p-3 rounded">
                        <span className="text-slate-600">Appeal Success:</span> <span className="font-semibold">{detailedFormData.appealSuccessRate}%</span>
                      </div>
                    </div>
                  </div>

                  {/* Financial Impact Charts */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
                    <div className="bg-slate-50 p-4 rounded-lg text-center">
                      <h4 className="text-sm font-semibold text-slate-700 mb-2">Staff Cost Savings</h4>
                      <p className="text-xs text-slate-500 mb-2">Audit preparation efficiency</p>
                      <div className="text-lg font-bold text-[#619ed3]">
                        ${(results.detailedCalculations?.staffCostSavings || 
                          ((parseInt(detailedFormData.annualAudits || "0") * parseInt(detailedFormData.auditPrepHours || "0") * parseInt(detailedFormData.auditStaffSalary || "0")) * 0.4)
                        ).toLocaleString()}
                      </div>
                      <p className="text-xs text-slate-500">Annual Savings</p>
                    </div>
                    <div className="bg-slate-50 p-4 rounded-lg text-center">
                      <h4 className="text-sm font-semibold text-slate-700 mb-2">Denial Management</h4>
                      <p className="text-xs text-slate-500 mb-2">Reduced revenue loss</p>
                      <div className="text-lg font-bold text-[#619ed3]">
                        ${(results.detailedCalculations?.denialManagement || 
                          ((parseInt(detailedFormData.monthlyClaims || "0") * 12 * (parseInt(detailedFormData.denialRate || "0") / 100) * parseInt(detailedFormData.deniedClaimValue || "0")) * 0.3)
                        ).toLocaleString()}
                      </div>
                      <p className="text-xs text-slate-500">Annual Savings</p>
                    </div>
                    <div className="bg-slate-50 p-4 rounded-lg text-center">
                      <h4 className="text-sm font-semibold text-slate-700 mb-2">Revenue Recovery</h4>
                      <p className="text-xs text-slate-500 mb-2">Improved appeal success</p>
                      <div className="text-lg font-bold text-[#619ed3]">
                        ${(results.detailedCalculations?.revenueRecovery || 
                          ((parseInt(detailedFormData.monthlyClaims || "0") * 12 * (parseInt(detailedFormData.denialRate || "0") / 100) * parseInt(detailedFormData.deniedClaimValue || "0")) * 0.2)
                        ).toLocaleString()}
                      </div>
                      <p className="text-xs text-slate-500">Additional Recovery</p>
                    </div>
                  </div>

                  {/* Total Financial Impact */}
                  <div className="bg-gradient-to-r from-[#1e3d3b] to-[#2a5a56] p-6 rounded-lg text-white text-center">
                    <h4 className="text-lg font-semibold mb-2">Total Annual Financial Impact</h4>
                    <div className="text-3xl font-bold">
                      ${(results.detailedCalculations?.netSavings || 
                        (
                          ((parseInt(detailedFormData.annualAudits || "0") * parseInt(detailedFormData.auditPrepHours || "0") * parseInt(detailedFormData.auditStaffSalary || "0")) * 0.4) +
                          ((parseInt(detailedFormData.monthlyClaims || "0") * 12 * (parseInt(detailedFormData.denialRate || "0") / 100) * parseInt(detailedFormData.deniedClaimValue || "0")) * 0.5)
                        )
                      ).toLocaleString()}
                    </div>
                    <p className="text-sm opacity-90">Combined savings and revenue recovery with Fownd</p>
                  </div>
                </div>
              </>
            )}

            {/* Key Benefits */}
            <div className="mb-8">
              <h2 className="text-xl font-bold text-slate-800 mb-4">Key Benefits</h2>
              <div className="space-y-3">
                <div className="flex items-start gap-3">
                  <div className="w-2 h-2 bg-[#619ed3] rounded-full mt-2"></div>
                  <p className="text-slate-700 text-sm leading-relaxed">Significant reduction in documentation time, allowing therapists to focus more on patient care</p>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-2 h-2 bg-[#619ed3] rounded-full mt-2"></div>
                  <p className="text-slate-700 text-sm leading-relaxed">Improved documentation accuracy leading to better compliance and reduced claim denials</p>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-2 h-2 bg-[#619ed3] rounded-full mt-2"></div>
                  <p className="text-slate-700 text-sm leading-relaxed">Enhanced patient retention through improved care quality and consistency</p>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-2 h-2 bg-[#619ed3] rounded-full mt-2"></div>
                  <p className="text-slate-700 text-sm leading-relaxed">Substantial cost savings through operational efficiency and reduced administrative burden</p>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="border-t-2 border-[#1e3d3b] pt-4 mt-8">
              <div className="flex justify-between items-center">
                <div>
                  <p className="text-slate-600 text-sm">Generated by Fownd ROI Calculator</p>
                  <p className="text-xs text-slate-500">Visit fownd.care for more information</p>
                </div>
                <div className="text-right">
                  <p className="text-xs text-slate-500">Contact us for a personalized demo</p>
                  <p className="text-[#1e3d3b] font-semibold text-sm">info@fownd.org</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReportModal; 